import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  __,
  always,
  any,
  compose,
  defaultTo,
  descend,
  difference,
  equals,
  filter,
  find,
  fromPairs,
  groupBy,
  has,
  identity,
  ifElse,
  includes,
  isNil,
  last,
  lensPath,
  lensProp,
  map,
  mergeLeft,
  over,
  pickAll,
  pipe,
  pluck,
  prop,
  propEq,
  propOr,
  reject,
  replace,
  sort,
  sortBy,
  toLower,
  toPairs,
  uniqBy,
  view,
} from "ramda";
import { LIBRARY_BASE_URL } from "props";
import { useCurrentOrgId, useCurrentUser, useIsAdmin } from "../auth/authUtils";
import { isBlank } from "./ext";
import { useQuery } from "@tanstack/react-query";
import { hasPdf } from "../creator-application/content/contentServices";

export const isAlbum = media => equals(media.type, "ALBUM");
export const isAudiobook = media => equals(media.type, "AUDIOBOOK");
export const isChapterBook = media =>
  media.type === "BOOK" &&
  media.mimeType === "application/biblioboard+compound_content";

export const objectDiff = media => (val, key) => !equals(media[key], val);

export const getType = media => {
  const { mediaType, type, contentType } = media;

  if (mediaType === "anthology") {
    return "anthology";
  } else if (mediaType === "collection") {
    return "collection";
  } else if (mediaType === "curation") {
    return "curation";
  } else if (mediaType === "module") {
    return "module";
  } else {
    if (includes("ARTICLE", [type, contentType])) {
      return "article";
    } else if (includes("AUDIO", [type, contentType])) {
      return "audio";
    } else if (includes("OTHER_DOCUMENT", [type, contentType])) {
      return "document";
    } else if (includes("IMAGE", [type, contentType])) {
      return "image";
    } else if (includes("VIDEO", [type, contentType])) {
      return "video";
    } else if (includes("AUDIOBOOK", [type, contentType])) {
      return "audiobook";
    } else if (includes("ALBUM", [type, contentType])) {
      return "album";
    } else if (includes("CHAPTER", [type, contentType])) {
      return "chapter";
    } else {
      return "book";
    }
  }
};

export const getThumbnailUrl = media => {
  const { mediaType } = media;

  let url;

  if (
    mediaType === "anthology" ||
    mediaType === "module" ||
    mediaType === "collection"
  ) {
    url = media.iconUrl;
  } else {
    if (media.type === "CHAPTER" && !isNil(media.parentContent)) {
      url = media.parentContent.thumbnailUrl;
    } else {
      url = media.thumbnailUrl;
    }
  }

  if (url) {
    return `${url}?cb=${new Date().getTime()}`;
  } else {
    return null;
  }
};

export const getTitle = media => {
  const { mediaType } = media;

  if (
    mediaType === "anthology" ||
    mediaType === "module" ||
    mediaType === "collection"
  ) {
    return media.name;
  } else {
    return media.title;
  }
};

export const getSubtitle = media => {
  const { mediaType } = media;

  if (mediaType === "anthology") {
    return media.subName;
  } else if (mediaType === "collection") {
    return media.description;
  } else if (mediaType === "module") {
    return media.displayName;
  } else {
    return media.subtitle;
  }
};

export const getDetailsUrl = media => {
  const { mediaType } = media;

  if (mediaType === "collection") {
    return `${LIBRARY_BASE_URL}/anthology-collection/${media.id}/${media.anthology.id}`;
  } else {
    return `${LIBRARY_BASE_URL}/${media.mediaType}/${media.id}`;
  }
};

const getAudiobookDownloadLink = media => {
  const baseUrl = media.audioStreamUrl.substr(
    0,
    media.audioStreamUrl.lastIndexOf("_"),
  );

  const params = media.audioStreamUrl.substr(media.audioStreamUrl.indexOf("?"));

  return baseUrl + ".zip" + params;
};

/**
 * Returns the URL for downloading content files stored in S3
 *
 * Those with permission (Admin and Creator Managers) should
 * be able to download any content file.
 * @param {Object} media
 * @returns {string|undefined} - URL for downloading content file
 */
export const getDownloadLink = async media => {
  if (!media) return undefined;

  if (media.type === "AUDIOBOOK") {
    return getAudiobookDownloadLink(media);
  }

  // We can't rely on mime type for Articles and Documents
  // Must check S3 for existence of pdf
  if (media.type === "ARTICLE" || media.type === "OTHER_DOCUMENT") {
    if (media.mimeType === "application/epub+zip") {
      return media.epubFileUrl;
    }

    if (await hasPdf(media)) {
      return media.pdfUrl;
    }

    return media.imageUrl || media.pdfUrl;
  }

  if (media.mimeType.startsWith("image/")) {
    if (media.type === "BOOK") {
      return media.pdfUrl || media.imageUrl || undefined;
    } else {
      return media.imageUrl || undefined;
    }
  }

  return (
    media.pdfUrl ||
    media.epubFileUrl ||
    media.audioStreamUrl ||
    prop("url", last(media.videoFormats || [])) ||
    undefined
  );
};

export const useDownloadLink = media =>
  useQuery({
    queryKey: ["download-link", media?.id, media?.pdfUrl, media?.imageUrl],
    queryFn: () => getDownloadLink(media),
    enabled: Boolean(media),
  });

export const getDownloadFileName = media => {
  return media.id.concat(media.pdfUrl ? ".pdf" : ".epub");
};

/**
 * Updating Content Metadata
 */
const setContentProp = (condition, prop, setterFn) =>
  ifElse(
    condition,
    update => over(lensProp(prop), () => setterFn(update), update),
    identity,
  );

/**
 * Purchase Link
 */
export const hasPurchaseLink = has("purchaseLink");
// PATCH request requires both purchaseLink.title and purchaseLink.url
export const getFormattedPurchaseLink = update => {
  const purchaseLink = pickAll(["title", "url"], prop("purchaseLink", update));
  return map(defaultTo(""), purchaseLink);
};
export const setPurchaseLink = setContentProp(
  hasPurchaseLink,
  "purchaseLink",
  getFormattedPurchaseLink,
);

/**
 * Publication Date & Publication Year
 */
export const hasPublicationYear = has("publicationYear");
const setFormattedPubDate = update =>
  `${update.publicationYear}-01-01T00:00:00Z`;

export const setPubDate = setContentProp(
  hasPublicationYear,
  "publicationDate",
  setFormattedPubDate,
);

/**
 * Product Identifiers
 */
export const hasProductIdentifiers = has("productIdentifiers");
// Sort identifier types in order of most relevant product form: DIGITAL, PRINT, no form
const productIdentifierSortOrder = {
  DIGITAL: 0,
  PRINT: 1,
  undefined: 2,
};

const getPriority = pipe(
  prop("productForm"),
  defaultTo(undefined),
  propOr(2, __, productIdentifierSortOrder),
);

// Map productIdentifier types to metadata props, e.g. "ISBN-13" -> "isbn"
const transformTypeKey = pipe(toLower, replace(/-13$/, ""));

const mapTypesToMetadataProps = pipe(
  toPairs,
  map(([key, value]) => [transformTypeKey(key), value]),
  fromPairs,
);

/**
 * Grab ISBN, ISSN, or DOI value from productIdentifiers list
 * Pass value to corresponding property saved on content
 * e.g. ISBN-13 -> content.isbn, DOI -> content.doi
 */
const getProductIdentifiersForMetadata = pipe(
  filter(identifier => includes(identifier.type, ["ISBN-13", "DOI", "ISSN"])),
  groupBy(prop("type")),
  map(sortBy(getPriority)),
  map(view(lensPath([0, "value"]))), // after sort, get first identifier value, e.g. [{"ISBN-13", "ISBN-13 value"}, ...]
  mapTypesToMetadataProps,
  pickAll(["doi", "isbn", "issn"]),
  map(ifElse(isNil, always(null), identity)), // sets doi, isbn, issn as null if not found in productIdentifiers list
);

export const setProductIdentifiers = update => {
  if (update.productIdentifiers) {
    update.productIdentifiers = reject(
      pipe(prop("value"), isBlank),
      update.productIdentifiers,
    );
    const metadata = getProductIdentifiersForMetadata(
      update.productIdentifiers,
    );
    // if doi, isbn, or issn are missing from metadata, then set default as null
    return mergeLeft(metadata, update);
  }

  return update;
};

//-- Format duration
//-- Pulled from Patron UI
const notZero = x => !equals(x, "0");
export const formatDuration = seconds => {
  if (seconds === parseInt(seconds, 10)) {
    const date = new Date(null);
    date.setSeconds(seconds);

    const isoString = date.toISOString();

    const tens = isoString.substring(11, 12);
    const ones = isoString.substring(12, 13);

    return any(notZero, [tens, ones])
      ? isoString.substring(11, 19)
      : isoString.substring(14, 19);
  }
};

export const deduplicate = uniqBy(prop("id"));
export const getIds = pluck("id");
export const sortByLastUpdated = sort(descend(prop("auditUpdated")));
export const sortByName = sortBy(compose(toLower, prop("name")));

export const setAspectRatio = media => {
  switch (media.mediaType) {
    case "module":
    case "anthology":
      return 1;
    case "collection":
      return 400 / 276;
    default:
      return null;
  }
};

// permissions based on org principal
export const useMediaPermissions = media => {
  const isAdmin = useIsAdmin();
  const currentOrgId = useCurrentOrgId();
  const user = useCurrentUser();

  const [state, setState] = useState({
    deletable: false,
    readOnly: true,
    permissions: [],
  });

  // ----------- COMPUTE NEXT VALUES WITHOUT CAUSING RERENDERS -----------
  const next = useMemo(() => {
    if (!media) return state; // No change

    // Global admin
    if (isAdmin) {
      return {
        deletable: false, // [Tyler] In the Organize screen, global admin cannot always delete (i.e. modules with subscriptions), so leaving false for now
        readOnly: false,
        permissions: ["ALL"],
      };
    }

    // Org admin?
    const userOrgs = user?.organizations ?? [];
    const userOrgRoles =
      find(propEq("id", currentOrgId), userOrgs)?.roles ?? [];

    if (includes("ROLE_ADMIN", userOrgRoles)) {
      return {
        deletable: true,
        readOnly: false,
        permissions: ["ALL"],
      };
    }

    // find permissions for current org id
    const p = find(
      propEq("principal", currentOrgId),
      media?.accessPermissions ?? [],
    );

    if (!p) return state; // no permissions found, so no change

    return {
      deletable: includes("DELETE", p.permissionList),
      readOnly: !includes("UPDATE", p.permissionList),
      permissions: p.permissionList,
    };
  }, [media, isAdmin, currentOrgId, user, state]);

  // ----------- ONLY UPDATE STATE IF SOMETHING ACTUALLY CHANGES -----------
  useEffect(() => {
    if (
      next.deletable !== state.deletable ||
      next.readOnly !== state.readOnly ||
      !arraysEqual(next.permissions, state.permissions)
    ) {
      setState(next);
    }
  }, [next, state]);

  return state;
};

// Utility
const arraysEqual = (a, b) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

export const PermissionChecker = ({ media, onPermissionCheck }) => {
  const permissions = useMediaPermissions(media);
  const [prevPermissions, setPrevPermissions] = useState([]);

  useEffect(() => {
    if (difference(permissions.permissions, prevPermissions).length > 1) {
      onPermissionCheck(media.id, permissions);
      setPrevPermissions(permissions.permissions);
    }
  }, [media, permissions, prevPermissions, onPermissionCheck]);

  return null;
};

/**
 *
 * @param {Object[]} media - an array of Media, i.e. Module, Anthology, Collection, or Content
 */
export const usePermissions = media => {
  const [permissions, setPermissions] = useState({});

  // Callback to receive permissions from PermissionChecker
  const handlePermissionCheck = useCallback((mediaId, permissions) => {
    setPermissions(prev => ({ ...prev, [mediaId]: permissions }));
  }, []);

  // Reset permissions when media changes
  useEffect(() => {
    if (media.length === 0) {
      setPermissions({});
    }
  }, [media]);

  return {
    permissions,
    PermissionComponents: media.map(item => (
      <PermissionChecker
        key={item.id}
        media={item}
        onPermissionCheck={handlePermissionCheck}
      />
    )),
  };
};
